import fetchBuilder, { RequestInitRetryParams } from 'fetch-retry';
import { decode } from 'js-base64';
import { uniqBy } from 'lodash-es';
import {
  AIDT_ACCESS_ID,
  AIDT_ACCESS_TOKEN,
  AIDT_API_DOMAIN,
  AIDT_LECTURE_CODE,
  AIDT_LOGIN_AUTH_PARTNER_ID,
  AIDT_LOGIN_AUTH_PARTNER_KEYWORD,
  AIDT_USER_ID,
  AIDT_USER_TYPE,
} from '@shared/constants/token';
import {
  API_CODES,
  MySchoolNameResponse,
  TeacherClassMembersResponse,
  TeacherScheduleResponse,
  StudentScheduleResponse,
  UserNamesResponse,
  ScheduleItem,
} from './type';
import Cookies from 'js-cookie';
import { gql } from '@apollo/client';
import { getApolloClient } from '@ui/utils/createApolloClient';
import {
  AidtKerisApi_GetUserIdsQuery,
  AidtKerisApi_GetUserIdsQueryVariables,
  AidtKerisApi_GetUserIdsDocument,
  AidtKerisApi_GetGroup_LectureCode_StudentDocument,
  AidtKerisApi_GetGroup_LectureCode_TeacherDocument,
  AidtKerisApi_GetGroup_LectureCode_StudentQuery,
  AidtKerisApi_GetGroup_LectureCode_TeacherQuery,
  AidtKerisApi_GetGroup_LectureCode_StudentQueryVariables,
  AidtKerisApi_GetGroup_LectureCode_TeacherQueryVariables,
} from './aidtKerisAPI.generated';
import { sortBy } from 'lodash-es';
import { sessionTokenInvalidate } from '@ui/components/recipes/AuthAidt/sessionTokenInvalidate';

const fetch = fetchBuilder(global.fetch);

type RetryParams = RequestInitRetryParams<typeof global.fetch>;

function omitTokenBody(body: any) {
  if (body) {
    const bodyObj = JSON.parse(body as string);
    delete bodyObj.access_token;
    return bodyObj;
  }
  return null;
}

// Wrapper function to track API calls with WhatapBrowserAgent
async function trackedFetch(url: string, options: RequestInit & RetryParams) {
  const startTime = performance.now();
  const apiPath = new URL(url).pathname;

  try {
    const response = await fetch(url, options);
    const endTime = performance.now();
    const duration = endTime - startTime;

    if (typeof window !== 'undefined' && window.WhatapBrowserAgent) {
      const responseData = await response.clone().json();

      window.WhatapBrowserAgent.addCustomEvent(`AIDT_KERIS_API:${apiPath}`, {
        contents: JSON.stringify({
          path: apiPath,
          method: options.method,
          status: response.status,
          responseCode: responseData?.code,
          body: omitTokenBody(options.body),
          // @ts-ignore
          clientVersion: window.$version,
        }),
        customDuration: Math.round(duration),
      });
    }

    return response;
  } catch (error) {
    if (typeof window !== 'undefined' && window.WhatapBrowserAgent) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      window.WhatapBrowserAgent.addCustomEvent(`AIDT_KERIS_API_ERROR:${apiPath}`, {
        contents: JSON.stringify({
          path: apiPath,
          method: options.method,
          body: omitTokenBody(options.body),
          // @ts-ignore
          error: error?.message,
          // @ts-ignore
          clientVersion: window.$version,
        }),
        customDuration: Math.round(duration),
      });
    }
    throw error;
  }
}

// 책장이면 lecture_code가 none이다. api 문서 참고
export const BOOKSHELF_LECTURE_CODE = 'none';

const retryParams: RetryParams = {
  retries: 2,
  retryDelay: 500,
  retryOn: async function (attempt, error, response) {
    if (attempt > 1) return false;
    if (error !== null || (response.status && response.status >= 400)) {
      return true;
    }

    // const data = await response?.json();

    // if (data?.code !== API_CODES.OK) {
    //   return true;
    // }
    return false;
  },
};

class AIDTKerisAPI {
  get apiDomain() {
    return Cookies.get(AIDT_API_DOMAIN);
  }
  async defaultHeaders() {
    return {
      'Partner-ID': await this.loadPartnerId(),
      'API-version': '2.3',
    };
  }

  get requestAuthInfo() {
    return {
      access_token: {
        token: Cookies.get(AIDT_ACCESS_TOKEN),
        access_id: Cookies.get(AIDT_ACCESS_ID),
      },
      user_id: Cookies.get(AIDT_USER_ID),
    };
  }

  async assertStudentSchedules() {
    // try {
    //   await trackedFetch(`${this.apiDomain}/aidt_userinfo/student/schedule`, {
    //     method: 'POST',
    //     headers: {
    //       ...this.defaultHeaders,
    //       'Content-Type': 'application/json',
    //     } as any,
    //     body: JSON.stringify({
    //       ...this.requestAuthInfo,
    //     }),
    //   });
    // } catch (error) {
    //   clearCookies();
    //   window.location.reload();
    //   throw error;
    // }
  }

  async loadPartnerId() {
    const partnerId = Cookies.get(AIDT_LOGIN_AUTH_PARTNER_ID);
    if (partnerId) return partnerId;

    const partnerKeyword = Cookies.get(AIDT_LOGIN_AUTH_PARTNER_KEYWORD);
    if (!partnerKeyword) return null;

    const partnerResponse = await trackedFetch(
      `${process.env.NEXT_PUBLIC_X_API_URL}/aidt-users/partner-id?partner_keyword=${partnerKeyword}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    const data = await partnerResponse.json();

    const newPartnerId = data?.partner_id;
    if (newPartnerId) Cookies.set(AIDT_LOGIN_AUTH_PARTNER_ID, newPartnerId);

    return newPartnerId;
  }

  async getTeacherLectureCodeUniqSchedules() {
    const teacherScheduleRes = await this.getTeacherSchedules();

    if (teacherScheduleRes?.code !== API_CODES.OK) {
      return [];
    }

    const scheduleInfos = uniqBy(teacherScheduleRes.schedule_info, (info) => info.lecture_code);

    return scheduleInfos;
  }

  async getTeacherLectureCodes(): Promise<string[]> {
    const lectureCode = Cookies.get(AIDT_LECTURE_CODE);
    if (lectureCode && lectureCode !== BOOKSHELF_LECTURE_CODE) return [lectureCode];
    const scheduleInfos = await this.getTeacherLectureCodeUniqSchedules();
    return scheduleInfos.map((info) => info.lecture_code);
  }

  async getTeacherSchedules() {
    const response = await trackedFetch(`${this.apiDomain}/aidt_userinfo/teacher/schedule`, {
      ...retryParams,
      method: 'POST',
      headers: {
        ...(await this.defaultHeaders()),
        'Content-Type': 'application/json',
      } as any,
      body: JSON.stringify({
        ...this.requestAuthInfo,
        // partner에 해당하는 시간표 필터링 여부
        user_id_schdule_yn: 'Y',
      }),
    });
    // check 403
    if (response.status === 403) {
      await sessionTokenInvalidate();
    }

    const data = await response.json();
    return data as TeacherScheduleResponse;
  }
  async getStudentSchedules() {
    const response = await trackedFetch(`${this.apiDomain}/aidt_userinfo/student/schedule`, {
      ...retryParams,
      method: 'POST',
      headers: {
        ...(await this.defaultHeaders()),
        'Content-Type': 'application/json',
      } as any,
      body: JSON.stringify({
        ...this.requestAuthInfo,
        // partner에 해당하는 시간표 필터링 여부
        user_id_schdule_yn: 'Y',
      }),
    });
    // check 403
    if (response.status === 403) {
      await sessionTokenInvalidate();
    }

    const data = await response.json();
    return data as StudentScheduleResponse;
  }

  async getMySchoolName() {
    const userType = Cookies.get(AIDT_USER_TYPE);
    let path = '';
    if (userType === 'T') {
      path = 'teacher';
    } else if (userType === 'S') {
      path = 'student';
    } else {
      return null;
    }

    const response = await trackedFetch(`${this.apiDomain}/aidt_userinfo/${path}/school_name`, {
      ...retryParams,
      method: 'POST',
      headers: {
        ...(await this.defaultHeaders()),
        'Content-Type': 'application/json',
      } as any,
      body: JSON.stringify({
        ...this.requestAuthInfo,
      }),
    });
    const data = await response.json();
    return data as MySchoolNameResponse;
  }

  async getTeacherClassMembers(lectureCode: string) {
    const response = await trackedFetch(`${this.apiDomain}/aidt_userinfo/teacher/class_member`, {
      ...retryParams,
      method: 'POST',
      headers: {
        ...(await this.defaultHeaders()),
        'Content-Type': 'application/json',
      } as any,
      body: JSON.stringify({
        ...this.requestAuthInfo,
        lecture_code: lectureCode,
      }),
    });
    const data = await response.json();
    return data as TeacherClassMembersResponse;
  }

  async getUserNames(userIdsArg: string[]): Promise<string[]> {
    const userIds = [...new Set(userIdsArg)];
    const { user_id, ...requestAuthInfo } = this.requestAuthInfo;
    const client = getApolloClient();

    const userIdMapQuery = await client.query<
      AidtKerisApi_GetUserIdsQuery,
      AidtKerisApi_GetUserIdsQueryVariables
    >({
      query: AidtKerisApi_GetUserIdsDocument,
      variables: {
        input: {
          userIds,
        },
      },
      fetchPolicy: 'network-only',
    });

    const aidtUserMap = sortBy(userIdMapQuery?.data?.aidtUserMap?.aidtUserMap ?? [], (user) =>
      userIds.indexOf(user.userId),
    );

    const aidtUserIds = aidtUserMap.map((user) => decode(user.aidtUserId));

    const response = await trackedFetch(`${this.apiDomain}/aidt_userinfo/student/name`, {
      method: 'POST',
      headers: {
        ...(await this.defaultHeaders()),
        'Content-Type': 'application/json',
        // 10 minutes
        'Cache-Control': 'max-age=600',
      } as any,
      body: JSON.stringify({
        ...requestAuthInfo,
        user_ids: aidtUserIds,
      }),
    });

    const data = (await response.json()) as UserNamesResponse;

    if (data.code === API_CODES.OK) {
      const missingUserIds = aidtUserIds.filter((id) => !data.user_list.some((user) => user.user_id === id));

      if (missingUserIds.length > 0) {
        const teacherResponse = await trackedFetch(`${this.apiDomain}/aidt_userinfo/teacher/name`, {
          method: 'POST',
          headers: {
            ...(await this.defaultHeaders()),
            'Content-Type': 'application/json',
            // 10 minutes
            'Cache-Control': 'max-age=600',
          } as any,
          body: JSON.stringify({
            ...requestAuthInfo,
            user_ids: missingUserIds,
          }),
        });

        const teacherData = (await teacherResponse.json()) as UserNamesResponse;
        if (teacherData.code === API_CODES.OK) {
          data.user_list = [...data.user_list, ...teacherData.user_list];
        }
      }

      data.user_list = sortBy(data.user_list, (user) => aidtUserIds.indexOf(user.user_id));

      return userIdsArg.map((originId) => {
        const aidtUserId = aidtUserMap.find((user) => user.userId === originId)?.aidtUserId;
        const existingUser = data.user_list.find((user) => user.user_id === decode(aidtUserId ?? ''));

        return existingUser?.user_name ?? '-';
      });
    } else if (aidtUserIds.length > 0) {
      const teacherResponse = await trackedFetch(`${this.apiDomain}/aidt_userinfo/teacher/name`, {
        method: 'POST',
        headers: {
          ...(await this.defaultHeaders()),
          'Content-Type': 'application/json',
          // 10 minutes
          'Cache-Control': 'max-age=600',
        } as any,
        body: JSON.stringify({
          ...requestAuthInfo,
          user_ids: aidtUserIds,
        }),
      });

      const teacherData = (await teacherResponse.json()) as UserNamesResponse;
      if (teacherData.code === API_CODES.OK) {
        return userIdsArg.map((originId) => {
          const aidtUserId = aidtUserMap.find((user) => user.userId === originId)?.aidtUserId;
          const existingUser = teacherData.user_list.find(
            (user) => user.user_id === decode(aidtUserId ?? ''),
          );
          return existingUser?.user_name ?? '-';
        });
      }
    }

    return userIdsArg.map(() => '-');
  }

  async getLectureCodeGroupNames(): Promise<{
    groupId: string;
    groupName: string
  }[]> {
    function infoToGroupName(info: ScheduleItem) {
      const isNumberString = Number(info.classroom_name);
      if (isNumberString) {
        return `${info.classroom_name}반`
      }

      return `${info.classroom_name}`
    }

    function processScheduleResponse(response: TeacherScheduleResponse | StudentScheduleResponse) {
      if (response?.code !== API_CODES.OK) {
        return [];
      }

      const scheduleInfos = uniqBy(response.schedule_info, (info) => info.lecture_code);
      return scheduleInfos.map((info) => ({
        lectureCode: info.lecture_code,
        groupName: infoToGroupName(info),
      }));
    }

    const userType = Cookies.get(AIDT_USER_TYPE);

    const client = getApolloClient();

    if (userType === 'T') {
      const groupLectureCodeQuery = await client.query<
        AidtKerisApi_GetGroup_LectureCode_TeacherQuery,
        AidtKerisApi_GetGroup_LectureCode_TeacherQueryVariables
      >({
        query: AidtKerisApi_GetGroup_LectureCode_TeacherDocument,
        fetchPolicy: 'cache-first',
      });

      const groups = groupLectureCodeQuery.data?.me?.manager?.groups ?? []

      const scheduleRes = await this.getTeacherSchedules();
      const groupNameByLectureCode = processScheduleResponse(scheduleRes);
      return groups.map(group => ({
        groupId: group.id,
        groupName: groupNameByLectureCode.find(g => g.lectureCode === group.aidtLecture?.lectureCode)?.groupName ?? '',
      }));
    } else if (userType === 'S') {
      const groupLectureCodeQuery = await client.query<
        AidtKerisApi_GetGroup_LectureCode_StudentQuery,
        AidtKerisApi_GetGroup_LectureCode_StudentQueryVariables
      >({
        query: AidtKerisApi_GetGroup_LectureCode_StudentDocument,
        fetchPolicy: 'cache-first',
      });

      const groups = groupLectureCodeQuery.data?.me?.groups ?? []

      const scheduleRes = await this.getStudentSchedules();
      const groupNameByLectureCode = processScheduleResponse(scheduleRes);
      return groups.map(group => ({
        groupId: group.id,
        groupName: groupNameByLectureCode.find(g => g.lectureCode === group.aidtLecture?.lectureCode)?.groupName ?? '',
      }));
    }

    return [];
  }
}

export const aidtKerisAPI = new AIDTKerisAPI();

gql`
  query AIDTKerisAPI_GetUserIds($input: AidtUserMapInput!) {
    aidtUserMap(input: $input) {
      aidtUserMap {
        aidtUserId
        userId
      }
    }
  }
`;
gql`
  query AIDTKerisAPI_GetGroup_LectureCode_Student {
     me {
     id
      groups {
        id
        aidtLecture {
          id
          lectureCode
        }
      }
    }
  }
`;
gql`
  query AIDTKerisAPI_GetGroup_LectureCode_Teacher {
     me {
      id
      manager {
        id
        groups {
          id
          aidtLecture {
            id
            lectureCode
          }
        }
      }
    }
  }
`;

